import { ArrowLeft, ChevronLeft, ChevronRight, Settings, ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import { SignalBadge } from '@/components/SignalBadge';
import { useLatestData, useLatestSignal, useSignals, useConfig } from '@/hooks/useApi';
import { formatScore, formatFixed, formatNumber, formatPrice } from '@/lib/format';
import type { Signal } from '@/types';

const SIGNAL_TYPES: Signal['type'][] = [
	'ENTRY',
	'EXIT',
	'STOP_LOSS',
	'TAKE_PROFIT',
	'ADJUSTMENT',
	'HOLD',
	'NO_ACTION',
	'TRAILING_STOP',
	'BLOCKED'
];

const typeConfig: Record<Signal['type'], { label: string }> = {
	ENTRY: { label: 'Entry' },
	EXIT: { label: 'Exit' },
	STOP_LOSS: { label: 'Stop Loss' },
	TAKE_PROFIT: { label: 'Take Profit' },
	ADJUSTMENT: { label: 'Adjustment' },
	HOLD: { label: 'Hold' },
	NO_ACTION: { label: 'No Action' },
	TRAILING_STOP: { label: 'Trailing Stop' },
	BLOCKED: { label: 'Blocked' }
};

export function MarketDetail() {
	const { symbol } = useParams<{ symbol: string }>();
	const decodedSymbol = decodeURIComponent(symbol || '');

	const { data: latestData, isLoading: dataLoading } = useLatestData(decodedSymbol);
	const { data: latestSignal, isLoading: signalLoading } = useLatestSignal(decodedSymbol);
	const { data: config } = useConfig();

	const isLoading = dataLoading || signalLoading;
	const indicators = latestData?.indicators;
	const signal = latestSignal?.signal;
	const symbolConfig = config?.symbolConfigs?.[decodedSymbol];

	const minAtrPct = symbolConfig?.MIN_ATR_PCT;

	const displaySymbol = decodedSymbol.replace('PERP_', '').replace('_USDC', '');

	// Signal history state
	const [selectedTypes, setSelectedTypes] = useState<Signal['type'][]>([]);
	const [cursorHistory, setCursorHistory] = useState<string[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
	const [showRawConfig, setShowRawConfig] = useState(false);

	// Initialize with all types selected
	useEffect(() => {
		if (selectedTypes.length === 0) {
			setSelectedTypes(SIGNAL_TYPES);
		}
	}, [selectedTypes.length]);

	const { data: signalsData, isLoading: signalsLoading } = useSignals(
		decodedSymbol,
		50,
		currentCursor,
		selectedTypes.length > 0 ? selectedTypes : undefined
	);
	const signals = signalsData?.signals || [];
	const totalCount = signalsData?.totalCount || 0;
	const hasMore = signalsData?.pagination?.hasMore || false;

	// Handle next page
	const handleNext = () => {
		if (signalsData?.pagination?.nextCursor) {
			if (currentCursor) {
				setCursorHistory((prev) => [...prev, currentCursor]);
			} else {
				setCursorHistory(['']);
			}
			setCurrentCursor(signalsData.pagination.nextCursor);
		}
	};

	// Handle previous page
	const handlePrev = () => {
		if (cursorHistory.length > 0) {
			const newHistory = [...cursorHistory];
			const prevCursor = newHistory.pop();
			setCursorHistory(newHistory);
			setCurrentCursor(prevCursor === '' ? undefined : prevCursor);
		}
	};

	const hasPrev = cursorHistory.length > 0;

	// Toggle type selection
	const toggleType = (type: Signal['type']) => {
		setSelectedTypes((prev) => {
			const isSelected = prev.includes(type);
			if (isSelected) {
				if (prev.length === 1) return prev;
				return prev.filter((t) => t !== type);
			}
			return [...prev, type];
		});
		setCurrentCursor(undefined);
		setCursorHistory([]);
	};

	// Select all types
	const selectAllTypes = () => {
		setSelectedTypes(SIGNAL_TYPES);
		setCurrentCursor(undefined);
		setCursorHistory([]);
	};

	const getTaScoreInterpretation = (score: number) => {
		if (score >= 2) return { label: 'STRONG BUY', color: 'bg-success text-white' };
		if (score >= 0.5) return { label: 'BUY', color: 'bg-success/80 text-white' };
		if (score >= -0.5) return { label: 'NEUTRAL', color: 'bg-text-muted text-white' };
		if (score >= -2) return { label: 'SELL', color: 'bg-danger/80 text-white' };
		return { label: 'STRONG SELL', color: 'bg-danger text-white' };
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Link
					to="/markets"
					className="p-2 text-text-muted hover:text-text transition-colors"
					aria-label="Back to market"
				>
					<ArrowLeft className="w-5 h-5" />
				</Link>
				<div>
					<h1 className="text-2xl font-bold text-text">{displaySymbol}</h1>
					<p className="text-text-muted mt-1">Technical indicators and analysis</p>
				</div>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center h-64">
					<div className="text-text-muted">Loading market data...</div>
				</div>
			) : (
				<>
					{/* Price and Signal */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="card">
							<p className="text-sm text-text-muted mb-2">Current Price</p>
							<p className="text-4xl font-bold text-text">
								${formatPrice(indicators?.candle?.close)}
							</p>
							<div className="mt-2 flex items-center gap-4 text-sm">
								<span className="text-text-muted">
									High: <span className="text-text">${formatPrice(indicators?.candle?.high)}</span>
								</span>
								<span className="text-text-muted">
									Low: <span className="text-text">${formatPrice(indicators?.candle?.low)}</span>
								</span>
								<span className="text-text-muted">
									Vol:{' '}
									<span className="text-text">
										{formatNumber(indicators?.candle?.volume ?? undefined, 0)}
									</span>
								</span>
							</div>
						</div>

						<div className="card">
							<p className="text-sm text-text-muted mb-2">Entry Score</p>
							<div className="flex items-center gap-4">
								<div className="text-4xl font-bold text-text">
									{signal?.taScore !== undefined ? formatScore(signal.taScore) : '-'}
								</div>
								{signal?.taScore !== undefined && (
									<span
										className={`px-3 py-1 rounded-lg text-sm font-medium ${getTaScoreInterpretation(signal.taScore).color}`}
									>
										{getTaScoreInterpretation(signal.taScore).label}
									</span>
								)}
							</div>
							{signal?.exitScore !== undefined ? (
								<p className="text-sm text-text-muted mt-2">
									Exit Score:{' '}
									<span className="font-medium text-text">{formatScore(signal.exitScore)}</span>{' '}
									<span className="text-text-muted">
										(exit threshold {formatFixed(signal.threshold)})
									</span>
								</p>
							) : (
								<p className="text-sm text-text-muted mt-2">
									Threshold: {signal?.threshold ?? '-'}
								</p>
							)}
						</div>
					</div>

					{/* Indicators Table */}
					<div className="card">
						<h2 className="text-lg font-semibold text-text mb-4">Technical Indicators</h2>
						<div className="overflow-x-auto">
							<table className="w-full">
								<thead className="bg-surface-hover">
									<tr>
										<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
											Indicator
										</th>
										<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
											Value
										</th>
										<th className="text-left py-3 px-4 text-sm font-medium text-text-muted">
											Signal
										</th>
									</tr>
								</thead>
								<tbody>
									<tr className="border-b border-border">
										<td className="py-3 px-4 text-text font-medium">RSI</td>
										<td className="py-3 px-4 text-text">
											{formatNumber(indicators?.rsi?.value ?? undefined)}
										</td>
										<td className="py-3 px-4">
											<span
												className={`text-sm ${
													(indicators?.rsi?.value || 50) > 70
														? 'text-danger'
														: (indicators?.rsi?.value || 50) < 30
															? 'text-success'
															: 'text-text-muted'
												}`}
											>
												{(indicators?.rsi?.value || 50) > 70
													? 'Overbought'
													: (indicators?.rsi?.value || 50) < 30
														? 'Oversold'
														: 'Neutral'}
											</span>
										</td>
									</tr>
									<tr className="border-b border-border">
										<td className="py-3 px-4 text-text font-medium">VWAP</td>
										<td className="py-3 px-4 text-text">{formatPrice(indicators?.vwap?.value)}</td>
										<td className="py-3 px-4">
											<span
												className={`text-sm ${
													(indicators?.candle?.close || 0) > (indicators?.vwap?.value || 0)
														? 'text-danger'
														: 'text-success'
												}`}
											>
												{(indicators?.candle?.close || 0) > (indicators?.vwap?.value || 0)
													? 'Above'
													: 'Below'}
											</span>
										</td>
									</tr>
									<tr className="border-b border-border">
										<td className="py-3 px-4 text-text font-medium">Bollinger Bands</td>
										<td className="py-3 px-4 text-text">
											U: {formatPrice(indicators?.bbands?.valueUpperBand)} | M:{' '}
											{formatPrice(indicators?.bbands?.valueMiddleBand)} | L:{' '}
											{formatPrice(indicators?.bbands?.valueLowerBand)}
										</td>
										<td className="py-3 px-4">
											<span
												className={`text-sm ${
													(indicators?.candle?.close || 0) >
													(indicators?.bbands?.valueUpperBand || 0)
														? 'text-danger'
														: (indicators?.candle?.close || 0) <
															  (indicators?.bbands?.valueLowerBand || 0)
															? 'text-success'
															: 'text-text-muted'
												}`}
											>
												{(indicators?.candle?.close || 0) >
												(indicators?.bbands?.valueUpperBand || 0)
													? 'Above Upper'
													: (indicators?.candle?.close || 0) <
														  (indicators?.bbands?.valueLowerBand || 0)
														? 'Below Lower'
														: 'Within Bands'}
											</span>
										</td>
									</tr>
									<tr className="border-b border-border">
										<td className="py-3 px-4 text-text font-medium">OBV</td>
										<td className="py-3 px-4 text-text">
											{formatNumber(indicators?.obv?.value ?? undefined, 0)}
										</td>
										<td className="py-3 px-4">
											<span className="text-sm text-text-muted">Volume momentum</span>
										</td>
									</tr>
									<tr>
										<td className="py-3 px-4 text-text font-medium">ATR</td>
										<td className="py-3 px-4 text-text">{formatPrice(indicators?.atr?.value)}</td>
										<td className="py-3 px-4">
											{indicators?.atr?.value &&
											indicators?.candle?.close &&
											symbolConfig?.ATR_SL_MULTIPLIER != null &&
											symbolConfig?.ATR_TP_MULTIPLIER != null ? (
												<span className="text-sm">
													<span className="text-danger">
														SL: $
														{formatPrice(
															indicators.candle.close -
																indicators.atr.value * symbolConfig.ATR_SL_MULTIPLIER
														)}
													</span>
													{' | '}
													<span className="text-success">
														TP: $
														{formatPrice(
															indicators.candle.close +
																indicators.atr.value * symbolConfig.ATR_TP_MULTIPLIER
														)}
													</span>
												</span>
											) : (
												<span className="text-sm text-text-muted">Volatility measure</span>
											)}
										</td>
									</tr>
									{indicators?.atr?.value && indicators?.candle?.close && (
										<tr>
											<td className="py-3 px-4 text-text font-medium">ATR%</td>
											<td className="py-3 px-4 text-text">
												{((indicators.atr.value / indicators.candle.close) * 100).toFixed(3)}%
											</td>
											<td className="py-3 px-4">
												{symbolConfig?.MIN_ATR_PCT !== undefined ? (
													<span className="text-sm">
														Min: {(symbolConfig.MIN_ATR_PCT * 100).toFixed(2)}%{' '}
														{indicators.atr.value / indicators.candle.close <
														symbolConfig.MIN_ATR_PCT ? (
															<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning/20 text-warning border border-warning/30 ml-2">
																GATE ACTIVE
															</span>
														) : (
															<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success/20 text-success border border-success/30 ml-2">
																OK
															</span>
														)}
													</span>
												) : (
													<span className="text-sm text-text-muted">ATR as % of price</span>
												)}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>

					{/* Signal Breakdown */}
					{signal?.indicators && (
						<div className="card">
							<h2 className="text-lg font-semibold text-text mb-4">Signal Breakdown</h2>
							<p className="text-xs text-text-muted mb-2 uppercase tracking-wide">Entry Scores</p>
							<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
								<div className="text-center p-3 bg-surface-hover rounded-lg">
									<p className="text-xs text-text-muted mb-1">VWAP</p>
									<p
										className={`text-lg font-bold ${signal.indicators.vwap >= 0 ? 'text-success' : 'text-danger'}`}
									>
										{formatScore(signal.indicators.vwap, 2)}
									</p>
								</div>
								<div className="text-center p-3 bg-surface-hover rounded-lg">
									<p className="text-xs text-text-muted mb-1">BBands</p>
									<p
										className={`text-lg font-bold ${signal.indicators.bbands >= 0 ? 'text-success' : 'text-danger'}`}
									>
										{formatScore(signal.indicators.bbands, 2)}
									</p>
								</div>
								<div className="text-center p-3 bg-surface-hover rounded-lg">
									<p className="text-xs text-text-muted mb-1">RSI</p>
									<p
										className={`text-lg font-bold ${signal.indicators.rsi >= 0 ? 'text-success' : 'text-danger'}`}
									>
										{formatScore(signal.indicators.rsi, 2)}
									</p>
								</div>
								<div className="text-center p-3 bg-surface-hover rounded-lg">
									<p className="text-xs text-text-muted mb-1">OBV</p>
									<p
										className={`text-lg font-bold ${signal.indicators.obv >= 0 ? 'text-success' : 'text-danger'}`}
									>
										{formatScore(signal.indicators.obv, 2)}
									</p>
								</div>
								<div className="text-center p-3 bg-primary/10 rounded-lg">
									<p className="text-xs text-text-muted mb-1">Total</p>
									<p
										className={`text-lg font-bold ${signal.indicators.total >= 0 ? 'text-success' : 'text-danger'}`}
									>
										{formatScore(signal.indicators.total)}
									</p>
								</div>
							</div>

							{signal.exitIndicators && (
								<>
									<p className="text-xs text-text-muted mt-4 mb-2 uppercase tracking-wide">
										Exit Scores (drives close decisions)
									</p>
									<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
										<div className="text-center p-3 bg-surface-hover rounded-lg">
											<p className="text-xs text-text-muted mb-1">VWAP</p>
											<p
												className={`text-lg font-bold ${signal.exitIndicators.vwap >= 0 ? 'text-success' : 'text-danger'}`}
											>
												{formatScore(signal.exitIndicators.vwap, 2)}
											</p>
										</div>
										<div className="text-center p-3 bg-surface-hover rounded-lg">
											<p className="text-xs text-text-muted mb-1">BBands</p>
											<p
												className={`text-lg font-bold ${signal.exitIndicators.bbands >= 0 ? 'text-success' : 'text-danger'}`}
											>
												{formatScore(signal.exitIndicators.bbands, 2)}
											</p>
										</div>
										<div className="text-center p-3 bg-surface-hover rounded-lg">
											<p className="text-xs text-text-muted mb-1">RSI</p>
											<p
												className={`text-lg font-bold ${signal.exitIndicators.rsi >= 0 ? 'text-success' : 'text-danger'}`}
											>
												{formatScore(signal.exitIndicators.rsi, 2)}
											</p>
										</div>
										<div className="text-center p-3 bg-surface-hover rounded-lg">
											<p className="text-xs text-text-muted mb-1">OBV</p>
											<p
												className={`text-lg font-bold ${signal.exitIndicators.obv >= 0 ? 'text-success' : 'text-danger'}`}
											>
												{formatScore(signal.exitIndicators.obv, 2)}
											</p>
										</div>
										<div className="text-center p-3 bg-primary/10 rounded-lg">
											<p className="text-xs text-text-muted mb-1">Total</p>
											<p
												className={`text-lg font-bold ${signal.exitIndicators.total >= 0 ? 'text-success' : 'text-danger'}`}
											>
												{formatScore(signal.exitIndicators.total)}
											</p>
										</div>
									</div>
								</>
							)}
						</div>
					)}

					{/* Raw Configuration */}
					{symbolConfig && (
						<div className="card">
							<button
								onClick={() => setShowRawConfig(!showRawConfig)}
								className="flex items-center justify-between w-full"
							>
								<div className="flex items-center gap-2">
									<Settings className="w-5 h-5 text-primary" />
									<h2 className="text-lg font-semibold text-text">Trading Configuration</h2>
								</div>
								<ChevronDown
									className={`w-5 h-5 text-text-muted transition-transform ${showRawConfig ? 'rotate-180' : ''}`}
								/>
							</button>

							{showRawConfig && (
								<div className="mt-4 space-y-4 border-t border-border pt-4">
									{/* Multipliers */}
									<div>
										<h3 className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
											Entry Indicator Multipliers
										</h3>
										<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">VWAP</p>
												<p className="font-mono text-sm">
													{symbolConfig.VWAP_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">BBands</p>
												<p className="font-mono text-sm">
													{symbolConfig.BBANDS_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">RSI</p>
												<p className="font-mono text-sm">
													{symbolConfig.RSI_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">OBV</p>
												<p className="font-mono text-sm">
													{symbolConfig.OBV_DIVERGENCE_MULTIPLIER.toFixed(3)}
												</p>
											</div>
										</div>
									</div>

									{/* Exit Multipliers */}
									<div>
										<h3 className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
											Exit Indicator Multipliers
										</h3>
										<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">VWAP</p>
												<p className="font-mono text-sm">
													{symbolConfig.EXIT_VWAP_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">BBands</p>
												<p className="font-mono text-sm">
													{symbolConfig.EXIT_BBANDS_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">RSI</p>
												<p className="font-mono text-sm">
													{symbolConfig.EXIT_RSI_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">OBV</p>
												<p className="font-mono text-sm">
													{symbolConfig.EXIT_OBV_DIVERGENCE_MULTIPLIER.toFixed(3)}
												</p>
											</div>
										</div>
									</div>

									{/* Thresholds */}
									<div>
										<h3 className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
											Risk Parameters
										</h3>
										<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Max Leverage</p>
												<p className="font-mono text-sm">{symbolConfig.MAX_LEVERAGE}x</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">ATR SL Multiplier</p>
												<p className="font-mono text-sm text-danger">
													{symbolConfig.ATR_SL_MULTIPLIER.toFixed(2)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">ATR TP Multiplier</p>
												<p className="font-mono text-sm text-success">
													{symbolConfig.ATR_TP_MULTIPLIER.toFixed(2)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Min Order</p>
												<p className="font-mono text-sm">${symbolConfig.MIN_ORDER_SIZE_USD}</p>
											</div>
											{symbolConfig.MIN_ATR_PCT !== undefined && (
												<div className="p-2 bg-surface-hover rounded">
													<p className="text-xs text-text-muted">Min ATR%</p>
													<p className="font-mono text-sm">
														{(symbolConfig.MIN_ATR_PCT * 100).toFixed(2)}%
													</p>
												</div>
											)}
										</div>
									</div>

									{/* Position Thresholds */}
									<div>
										<h3 className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
											Position Thresholds
										</h3>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted mb-1">Long Position</p>
												<div className="space-y-1 text-sm">
													<div className="flex justify-between">
														<span className="text-text-muted">Open:</span>
														<span className="font-mono">
															{symbolConfig.POSITION_THRESHOLDS.long.open.toFixed(4)}
														</span>
													</div>
													<div className="flex justify-between">
														<span className="text-text-muted">Close:</span>
														<span className="font-mono">
															{symbolConfig.POSITION_THRESHOLDS.long.close.toFixed(4)}
														</span>
													</div>
												</div>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted mb-1">Short Position</p>
												<div className="space-y-1 text-sm">
													<div className="flex justify-between">
														<span className="text-text-muted">Open:</span>
														<span className="font-mono">
															{symbolConfig.POSITION_THRESHOLDS.short.open.toFixed(4)}
														</span>
													</div>
													<div className="flex justify-between">
														<span className="text-text-muted">Close:</span>
														<span className="font-mono">
															{symbolConfig.POSITION_THRESHOLDS.short.close.toFixed(4)}
														</span>
													</div>
												</div>
											</div>
										</div>
									</div>

									{/* Technical Parameters */}
									<div>
										<h3 className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
											Technical Parameters
										</h3>
										<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">VWAP Threshold</p>
												<p className="font-mono text-sm">
													{symbolConfig.VWAP_THRESHOLD.toFixed(4)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">OBV Window</p>
												<p className="font-mono text-sm">{symbolConfig.OBV_WINDOW_SIZE}</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Slope Threshold</p>
												<p className="font-mono text-sm">
													{symbolConfig.SLOPE_THRESHOLD.toFixed(5)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Consensus Required</p>
												<p className="font-mono text-sm">
													{symbolConfig.REQUIRE_INDICATOR_CONSENSUS ? 'Yes' : 'No'}
												</p>
											</div>
										</div>
									</div>

									{/* Signal & Scoring Parameters */}
									<div>
										<h3 className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
											Signal & Scoring Parameters
										</h3>
										<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Consecutive Signals</p>
												<p className="font-mono text-sm">
													{symbolConfig.CONSECUTIVE_SIGNAL_THRESHOLD}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Position Adj %</p>
												<p className="font-mono text-sm">
													{symbolConfig.POSITION_ADJUSTMENT_THRESHOLD_PERCENT.toFixed(2)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Profit Score Multiplier</p>
												<p className="font-mono text-sm">
													{symbolConfig.PROFIT_SCORE_MULTIPLIER.toFixed(3)}
												</p>
											</div>
											<div className="p-2 bg-surface-hover rounded">
												<p className="text-xs text-text-muted">Time Decay Multiplier</p>
												<p className="font-mono text-sm">
													{symbolConfig.TIME_DECAY_MULTIPLIER.toFixed(4)}
												</p>
											</div>
										</div>
									</div>

									{/* Raw JSON Toggle */}
									<details className="mt-4">
										<summary className="text-sm text-text-muted cursor-pointer hover:text-text transition-colors">
											View Raw JSON
										</summary>
										<pre className="mt-2 p-3 bg-surface rounded text-xs font-mono text-text-muted overflow-x-auto">
											{JSON.stringify(symbolConfig, null, 2)}
										</pre>
									</details>
								</div>
							)}
						</div>
					)}

					{/* Signal History */}
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h2 className="text-lg font-semibold text-text">Signal History</h2>
							<p className="text-sm text-text-muted">{totalCount} total signals</p>
						</div>

						{/* Filter by Type */}
						<div className="card">
							<div className="flex items-center justify-between mb-4">
								<h3 className="font-semibold text-text">Filter by Type</h3>
								<button
									onClick={selectAllTypes}
									className="text-sm text-primary hover:text-primary-hover transition-colors"
									disabled={selectedTypes.length === SIGNAL_TYPES.length}
								>
									Select All
								</button>
							</div>
							<div className="flex flex-wrap gap-3">
								{SIGNAL_TYPES.map((type) => {
									const isSelected = selectedTypes.includes(type);
									return (
										<label
											key={type}
											className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
												isSelected
													? 'bg-surface-hover ring-2 ring-primary'
													: 'bg-surface opacity-50 hover:opacity-75'
											}`}
										>
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => toggleType(type)}
												className="w-4 h-4 rounded border-border bg-surface text-primary focus:ring-primary focus:ring-offset-0"
											/>
											<span
												className={`text-sm font-medium ${isSelected ? 'text-text' : 'text-text-muted'}`}
											>
												{typeConfig[type].label}
											</span>
										</label>
									);
								})}
							</div>
						</div>

						{signalsLoading ? (
							<div className="flex items-center justify-center h-32">
								<div className="text-text-muted">Loading signals...</div>
							</div>
						) : signals.length === 0 ? (
							<div className="card text-center py-12">
								<p className="text-text-muted">No signals found</p>
								<p className="text-sm text-text-muted mt-2">
									Signals will appear here when trading conditions are met
								</p>
							</div>
						) : (
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<p className="text-sm text-text-muted">
										Showing {signals.length} of {totalCount} signals
									</p>
									{/* Pagination Controls */}
									<div className="flex items-center gap-2">
										<button
											onClick={handlePrev}
											disabled={!hasPrev || signalsLoading}
											className="p-1 rounded hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed text-text-muted"
										>
											<ChevronLeft className="w-5 h-5" />
										</button>
										<span className="text-sm text-text-muted px-2">
											Page {cursorHistory.length + 1}
										</span>
										<button
											onClick={handleNext}
											disabled={!hasMore || signalsLoading}
											className="p-1 rounded hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed text-text-muted"
										>
											<ChevronRight className="w-5 h-5" />
										</button>
									</div>
								</div>

								{signals.map((signalItem: Signal, index: number) => (
									<SignalBadge
										key={`${signalItem.timestamp}-${index}`}
										signal={signalItem}
										minAtrPct={minAtrPct}
									/>
								))}

								{/* Bottom Pagination Controls */}
								<div className="flex items-center justify-center gap-2 pt-4">
									<button
										onClick={handlePrev}
										disabled={!hasPrev || signalsLoading}
										className="px-4 py-2 rounded-lg bg-surface text-text-muted hover:text-text hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
									>
										← Previous
									</button>
									<span className="text-sm text-text-muted px-4">
										Page {cursorHistory.length + 1}
									</span>
									<button
										onClick={handleNext}
										disabled={!hasMore || signalsLoading}
										className="px-4 py-2 rounded-lg bg-surface text-text-muted hover:text-text hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
									>
										Next →
									</button>
								</div>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
